import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Check, ArrowRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { LOGO } from "@/data/mock";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { GenesisLandingCard } from "@/components/genesis/GenesisLandingCard";
import { COMPANY } from "@/lib/company";
import { PhoneLink } from "@/components/app/PhoneLink";

const HERO_IMG = "/hero-yacht.jpg";

// TODO: replace with the real deployed $AURAMAX Solana contract address.
const CONTRACT_ADDRESS = "CONTRACT_ADDRESS_COMING_SOON";
const CONTRACT_SET = CONTRACT_ADDRESS !== "CONTRACT_ADDRESS_COMING_SOON";

function truncate(addr) {
  if (!CONTRACT_SET) return "Contract address coming soon";
  if (addr.length <= 16) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-6)}`;
}

function ContractCopy({ full = false, className }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!CONTRACT_SET) return;
    try { await navigator.clipboard.writeText(CONTRACT_ADDRESS); } catch (e) { console.error("clipboard copy failed", e); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      onClick={copy}
      disabled={!CONTRACT_SET}
      data-testid="contract-copy"
      className={cn(
        "group flex items-center gap-3 rounded-full border border-am-border bg-am-surface/80 px-4 py-2.5 backdrop-blur-md transition-colors",
        CONTRACT_SET && "hover:border-am-border-strong",
        className
      )}
    >
      <span className={cn("font-mono text-xs text-am-text2 sm:text-sm", full && CONTRACT_SET && "hidden sm:inline")}>
        {full && CONTRACT_SET ? CONTRACT_ADDRESS : truncate(CONTRACT_ADDRESS)}
      </span>
      {full && CONTRACT_SET && <span className="font-mono text-xs text-am-text2 sm:hidden">{truncate(CONTRACT_ADDRESS)}</span>}
      <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full", copied ? "text-aura-mint" : "text-am-text3 group-hover:text-white")}>
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </span>
      {copied && <span data-testid="contract-copied" className="text-xs font-semibold text-aura-mint">Copied</span>}
    </button>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/home", { replace: true });
  }, [ready, user, navigate]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const join = () => navigate("/join");
  const login = () => navigate("/login");

  return (
    <div className="min-h-screen bg-am-bg text-am-text">
      {/* NAV */}
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-all duration-300",
          scrolled ? "border-b border-am-border bg-am-bg/80 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
        )}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <button onClick={() => navigate("/")} data-testid="nav-logo" className="flex items-center">
            <Logo size={34} />
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={login} data-testid="nav-login" className="rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10">
              Log In
            </button>
            <button onClick={join} data-testid="nav-join" className="rounded-full bg-white px-4 py-2 text-sm font-bold text-black transition-transform hover:scale-[1.04] sm:px-5">
              Join AuraMax
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative h-[100svh] min-h-[620px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="AuraMax" className="hero-zoom h-full w-full object-cover" />
        </div>
        {/* cinematic overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-am-bg via-am-bg/30 to-am-bg/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-am-bg/70 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_120%,rgba(6,7,8,0.9),transparent)]" />

        <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-14 sm:px-8 sm:pb-20">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-am-text2 sm:text-xs"
          >
            The social network for personal brands
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.18 }}
            className="font-display text-6xl font-extrabold leading-[0.9] tracking-tight text-white sm:text-8xl lg:text-9xl"
          >
            MAX YOUR<br /><span className="aura-text">AURA.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.32 }}
            className="mt-6 max-w-md text-base text-am-text2 sm:text-lg"
          >
            Create your identity. Build your audience. Own your presence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.44 }}
            className="mt-8 flex items-center gap-5"
          >
            <button onClick={join} data-testid="hero-join" className="group flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-black transition-transform hover:scale-[1.04]">
              JOIN AURAMAX <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={login} data-testid="hero-login" className="text-sm font-semibold uppercase tracking-wider text-white/90 hover:text-white">
              Log In
            </button>
          </motion.div>

          {/* token strip */}
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.58 }}
            className="mt-10 flex w-full max-w-xl items-center gap-4 rounded-2xl border border-am-border bg-am-bg/50 p-3 backdrop-blur-xl"
          >
            <img src={LOGO} alt="$AURAMAX" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-bold text-white">AuraMax</span>
                <span className="text-sm font-bold aura-text">$AURAMAX</span>
              </div>
              <div className="mt-1"><ContractCopy /></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — WHAT IS AURAMAX */}
      <section className="relative overflow-hidden px-5 py-28 sm:px-8 sm:py-40">
        <div className="pointer-events-none absolute -left-40 top-1/2 h-[520px] w-[520px] -translate-y-1/2 rounded-full opacity-[0.12] blur-3xl aura-radial" />
        <div className="relative mx-auto max-w-4xl">
          <motion.h2
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.7 }}
            className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            YOUR IDENTITY.<br />YOUR AUDIENCE.<br /><span className="aura-text">YOUR AURA.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-8 max-w-2xl text-lg leading-relaxed text-am-text2 sm:text-xl"
          >
            AuraMax is a social platform built around people. Create content, build your audience, go live and turn your online identity into something uniquely yours.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.24 }}
            className="mt-6 text-base font-semibold text-white"
          >
            One identity. One official presence.
          </motion.p>
          <GenesisLandingCard />
        </div>
      </section>

      {/* SECTION 3 — OFFICIAL TOKEN */}
      <section className="border-t border-am-border px-5 py-28 sm:px-8 sm:py-36">
        <div className="mx-auto grid max-w-5xl items-center gap-14 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="relative mx-auto flex aspect-square w-full max-w-sm items-center justify-center"
          >
            <div className="absolute inset-0 rounded-full opacity-40 blur-3xl aura-radial" />
            <img src={LOGO} alt="$AURAMAX" className="relative w-2/3 rounded-3xl object-contain drop-shadow-2xl" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-am-text2">The native token</p>
            <h2 className="mt-3 font-display text-5xl font-extrabold tracking-tight aura-text sm:text-6xl">$AURAMAX</h2>
            <p className="mt-4 max-w-md text-base text-am-text2 sm:text-lg">The official native token of the AuraMax ecosystem.</p>

            <div className="mt-8 space-y-px overflow-hidden rounded-2xl border border-am-border">
              <Row label="Official Name" value="AuraMax" />
              <Row label="Ticker" value="$AURAMAX" accent />
              <Row label="Network" value="Solana" />
              <div className="flex flex-col gap-2 bg-am-surface px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm text-am-text3">Contract</span>
                <ContractCopy full />
              </div>
            </div>
            {/* room reserved for future token links/buttons */}
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-am-border px-5 py-32 text-center sm:px-8 sm:py-44">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.14] blur-3xl aura-radial" />
        <motion.h2
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="relative font-display text-5xl font-extrabold tracking-tight text-white sm:text-7xl lg:text-8xl"
        >
          MAX YOUR <span className="aura-text">AURA.</span>
        </motion.h2>
        <div className="relative mt-10 flex flex-col items-center gap-4">
          <button onClick={join} data-testid="final-join" className="rounded-full bg-white px-10 py-4 text-sm font-bold text-black transition-transform hover:scale-[1.04]">
            JOIN AURAMAX
          </button>
          <button onClick={login} data-testid="final-login" className="text-sm text-am-text2 hover:text-white">
            Already have an account? <span className="font-semibold text-white">Log in</span>
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-am-border px-5 py-10 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 sm:flex-row">
          <Logo size={26} />
          <div className="flex flex-col items-center gap-1 text-xs text-am-text3 sm:items-start" data-testid="footer-company">
            <span>{COMPANY.legalName} · Business Inquiries &amp; Support</span>
            <div className="flex items-center gap-3"><PhoneLink testid="footer-phone" className="text-xs" /><button onClick={() => navigate("/contact")} data-testid="footer-contact" className="hover:text-white">Contact</button></div>
          </div>
          <div className="flex items-center gap-6 text-xs text-am-text3">
            <span>© AuraMax</span>
            <button onClick={() => navigate("/terms")} data-testid="footer-terms" className="hover:text-white">Terms</button>
            <button onClick={() => navigate("/privacy")} data-testid="footer-privacy" className="hover:text-white">Privacy</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between bg-am-surface px-4 py-3.5">
      <span className="text-sm text-am-text3">{label}</span>
      <span className={cn("text-sm font-bold", accent ? "aura-text" : "text-white")}>{value}</span>
    </div>
  );
}
